import { useMemo } from 'react';
import { Box, Container } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import { Link, useSearchParams } from 'react-router-dom';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import {
  KC_HOME_PATH,
  KC_NAVIGATOR_PATH,
  KC_TOOLS_PATH,
  getNavigatorCatalog,
  getNavigatorJourney,
  listNavigatorJourneys,
  listNavigatorPackages,
  listNavigatorRoles,
} from './repository';
import { KC_COLORS } from './theme';
import './knowledgeCenter.css';

const PAGE_SIZE = 12;

function PackageCard({ item, toolsOnly }) {
  if (toolsOnly) {
    return (
      <Link to={item.detailPath} className="kc-pkg-card kc-pkg-card--tool">
        <div className="kc-pkg-tool-top">
          <span className="kc-pkg-tool-icon" aria-hidden>
            <HandymanOutlinedIcon sx={{ fontSize: 20 }} />
          </span>
          <span className="kc-pkg-card-type">Tool</span>
        </div>
        <h2>{item.title}</h2>
        <p className="kc-pkg-card-copy">{item.purpose}</p>
        <div className="kc-pkg-tool-foot">
          <span className="kc-pkg-card-open">
            Open
            <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link to={item.detailPath} className="kc-pkg-card">
      <span className="kc-pkg-card-type">{item.resource_type}</span>
      <h2>{item.title}</h2>
      <p className="kc-pkg-card-copy">{item.purpose}</p>
      <div className="kc-pkg-card-meta">
        <span>{item.category}</span>
        <span>{item.stage}</span>
      </div>
    </Link>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="kc-pkg-filter">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Pagination({ page, pageCount, total, pageSize, onPage }) {
  if (total === 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);

  return (
    <div className="kc-pkg-pagination">
      <p>
        Showing <strong>{start}–{end}</strong> of <strong>{total}</strong>
      </p>
      {pageCount > 1 ? (
        <div className="kc-pkg-page-btns">
          <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            Previous
          </button>
          {pages.map((number) => (
            <button
              key={number}
              type="button"
              className={number === page ? 'is-current' : ''}
              onClick={() => onPage(number)}
            >
              {number}
            </button>
          ))}
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => onPage(page + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function KnowledgeCenterNavigator({ toolsOnly = false }) {
  const [params, setParams] = useSearchParams();
  const catalog = getNavigatorCatalog();
  const journeys = listNavigatorJourneys();
  const roles = listNavigatorRoles();

  const filters = useMemo(
    () => ({
      q: params.get('q') || '',
      type: toolsOnly ? 'Tool' : 'Center',
      category: params.get('category') || '',
      stage: params.get('stage') || '',
      role: params.get('role') || '',
      journey: toolsOnly ? '' : params.get('journey') || '',
      resource: toolsOnly ? params.get('resource') || '' : '',
    }),
    [params, toolsOnly]
  );

  const packages = useMemo(() => listNavigatorPackages(filters), [filters]);
  const journey = getNavigatorJourney(filters.journey);
  const pageCount = Math.max(1, Math.ceil(packages.length / PAGE_SIZE) || 1);
  const requestedPage = Number(params.get('page') || 1);
  const page = Number.isFinite(requestedPage)
    ? Math.min(Math.max(1, requestedPage), pageCount)
    : 1;
  const pageItems = packages.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const updateParams = (mutator) => {
    const next = new URLSearchParams(params);
    mutator(next);
    setParams(next, { replace: true });
  };

  const updateFilter = (key, value) => {
    updateParams((next) => {
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
    });
  };

  const clearFilters = () => setParams({}, { replace: true });

  const goToPage = (nextPage) => {
    updateParams((next) => {
      if (nextPage <= 1) next.delete('page');
      else next.set('page', String(nextPage));
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fieldClass = 'kc-pkg-field';

  return (
    <>
      <PageSeoHelmet
        pageTitle={
          toolsOnly
            ? 'Downloadable Tools | Insurance AI Knowledge Center | Charlee.ai'
            : 'Insurance AI Knowledge Center Navigator | Charlee.ai'
        }
        metaDescription={
          toolsOnly
            ? 'Browse practical assessments, calculators, worksheets, templates, and playbooks for insurance AI.'
            : 'Find the right insurance AI implementation center by role, objective, and stage of adoption.'
        }
        canonicalPath={toolsOnly ? KC_TOOLS_PATH : KC_NAVIGATOR_PATH}
      />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain className="knowledge-center-navigator-page">
          <Box
            className="w-full pb-16 md:pb-20"
            style={{
              background:
                'linear-gradient(180deg, #F4F8FA 0%, #FFFFFF 18%, #FFFFFF 100%)',
            }}
          >
            <Container maxWidth="lg">
              <div className="pt-24 md:pt-28 text-left">
                <nav aria-label="Breadcrumb" className="mb-4">
                  <ol
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold"
                    style={{ color: KC_COLORS.navy700, fontSize: '14px' }}
                  >
                    <li>
                      <Link to="/" className="no-underline" style={{ color: KC_COLORS.navy700 }}>
                        Home
                      </Link>
                    </li>
                    <li style={{ color: KC_COLORS.gray600 }}>&gt;</li>
                    <li>
                      <Link
                        to={KC_HOME_PATH}
                        className="no-underline"
                        style={{ color: KC_COLORS.navy700 }}
                      >
                        Knowledge Center
                      </Link>
                    </li>
                    <li style={{ color: KC_COLORS.gray600 }}>&gt;</li>
                    <li style={{ color: KC_COLORS.gray600 }}>
                      {toolsOnly ? 'Tools' : 'Navigator'}
                    </li>
                  </ol>
                </nav>

                <div className="kc-pkg-list-head">
                  <div>
                    <p
                      className="m-0 kc-type-caption font-bold uppercase tracking-[0.08em]"
                      style={{ color: KC_COLORS.teal }}
                    >
                      {toolsOnly ? 'Practical toolkit' : 'Navigator'}
                    </p>
                    <h1
                      className="kc-type-h1 m-0 mt-1 break-words"
                      style={{ color: KC_COLORS.primaryNavy }}
                    >
                      {toolsOnly ? 'Downloadable Tools' : 'Knowledge Center Navigator'}
                    </h1>
                  </div>
                  {toolsOnly ? (
                    <Link
                      to={KC_NAVIGATOR_PATH}
                      className="kc-navigator-btn kc-navigator-btn-primary"
                    >
                      Explore the Navigator
                      <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                    </Link>
                  ) : (
                    <Link
                      to={KC_TOOLS_PATH}
                      className="kc-navigator-btn kc-navigator-btn-secondary"
                    >
                      Browse Downloadable Tools
                      <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                    </Link>
                  )}
                </div>

                <div className="kc-pkg-controls">
                  <div className="kc-pkg-search-row">
                    <FilterField label="Search">
                      <input
                        className={fieldClass}
                        value={filters.q}
                        onChange={(event) => updateFilter('q', event.target.value)}
                        placeholder={toolsOnly ? 'Search tools' : 'Search centers'}
                      />
                    </FilterField>
                  </div>
                  <div className={`kc-pkg-filter-row ${toolsOnly ? 'kc-pkg-filter-row--tools' : ''}`}>
                    <FilterField label="Coverage">
                      <select
                        className={fieldClass}
                        value={filters.category}
                        onChange={(event) => updateFilter('category', event.target.value)}
                      >
                        <option value="">All coverage</option>
                        {catalog.categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </FilterField>
                    <FilterField label="Stage">
                      <select
                        className={fieldClass}
                        value={filters.stage}
                        onChange={(event) => updateFilter('stage', event.target.value)}
                      >
                        <option value="">All stages</option>
                        {catalog.stages.map((stage) => (
                          <option key={stage} value={stage}>
                            {stage}
                          </option>
                        ))}
                      </select>
                    </FilterField>
                    <FilterField label="Audience">
                      <select
                        className={fieldClass}
                        value={filters.role}
                        onChange={(event) => updateFilter('role', event.target.value)}
                      >
                        <option value="">All audiences</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </FilterField>
                    {toolsOnly ? null : (
                      <FilterField label="Journey">
                        <select
                          className={fieldClass}
                          value={filters.journey}
                          onChange={(event) => updateFilter('journey', event.target.value)}
                        >
                          <option value="">All journeys</option>
                          {journeys.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </FilterField>
                    )}
                    <button type="button" className="kc-pkg-clear" onClick={clearFilters}>
                      Clear
                    </button>
                  </div>
                </div>

                {journey ? (
                  <p className="kc-pkg-hint">
                    <strong>{journey.goal}.</strong> {journey.recommend}
                  </p>
                ) : null}

                {packages.length ? (
                  <>
                    <div className="kc-pkg-grid">
                      {pageItems.map((item) => (
                        <PackageCard key={item.id} item={item} toolsOnly={toolsOnly} />
                      ))}
                    </div>
                    <Pagination
                      page={page}
                      pageCount={pageCount}
                      total={packages.length}
                      pageSize={PAGE_SIZE}
                      onPage={goToPage}
                    />
                  </>
                ) : (
                  <p className="mt-8" style={{ color: KC_COLORS.gray600 }}>
                    No resources match these filters.
                  </p>
                )}
              </div>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    </>
  );
}

export function KnowledgeCenterNavigatorPage() {
  return <KnowledgeCenterNavigator />;
}

export function KnowledgeCenterToolsPage() {
  return <KnowledgeCenterNavigator toolsOnly />;
}
